import { useState, useEffect, useRef } from 'react';
import { FormattedMessage } from 'react-intl';
import { clsx } from 'clsx';
import { isMobile, resolvePlatform } from '../../../../utils/platformResolver';
import { lockBackgroundScroll, unlockBackgroundScroll } from '../../../../utils/lockBackgroundScroll';
import { paramsKeys } from '../../../../utils/consts/params';
import Modal from '../../../common/Modal';
import SectionsSwitch from '../../../common/SectionSwitch';
import DesktopInstallCol from './DownloadModalComponents/DesktopInstallCol';
import MobileInstallCol from './DownloadModalComponents/MobileInstallCol';
import DesktopPairCol from './DownloadModalComponents/DesktopPairCol';
import MobilePairCol from './DownloadModalComponents/MobilePairCol';
import DownloadModalRouterSlide from './DownloadModalComponents/DownloadModalRouterSlide';
import styles from './DownloadModal.scss';

const slides = {
  DESKTOP: 'Desktop',
  MOBILE: 'Mobile',
  ROUTER: 'Router',
} as const;

type SlideType = (typeof slides)[keyof typeof slides];

export const downloadModalSlideLabels = slides;

interface DownloadModalProps {
  isOpen: boolean;
  onClose: () => void;
  downloadToken: string;
  requestNewDownloadToken: () => Promise<void>;
  onRouterSlideClick: () => void;
  isSetappUser?: boolean;
}

const getPresetSlideFromUrl = (): SlideType | undefined => {
  const param = new URLSearchParams(window.location.search).get(paramsKeys.DOWNLOAD_MODAL_SLIDE);

  return Object.values(slides).find((slide) => slide === param);
};

const DownloadModal = ({
  isOpen,
  onClose,
  downloadToken,
  requestNewDownloadToken,
  onRouterSlideClick,
  isSetappUser,
}: DownloadModalProps) => {
  const platform = useRef(resolvePlatform()).current;

  const [availableSlides, setAvailableSlides] = useState<SlideType[]>(Object.values(slides));
  const [selectedSlide, setSelectedSlide] = useState<SlideType>(isSetappUser ? slides.ROUTER : slides.DESKTOP);

  useEffect(() => {
    if (isOpen) {
      lockBackgroundScroll();
    } else {
      unlockBackgroundScroll();
    }
  }, [isOpen]);

  useEffect(() => {
    const presetSlide = getPresetSlideFromUrl();
    const isDesktop = ['macos', 'windows'].includes(platform ?? '') || !isMobile();

    if (isSetappUser) {
      setAvailableSlides([slides.ROUTER]);
      setSelectedSlide(slides.ROUTER);
    } else {
      setAvailableSlides(Object.values(slides));

      if (presetSlide) {
        setSelectedSlide(presetSlide);
      } else if (isDesktop) {
        setSelectedSlide(slides.DESKTOP);
      } else {
        setSelectedSlide(slides.MOBILE);
      }
    }
  }, [isSetappUser]);

  if (!isOpen) {
    return null;
  }

  return (
    <Modal withCloseButton isOpen={isOpen} className={styles.dialog} onClose={onClose}>
      <div className={styles.container}>
        <h2 className={styles.title}>
          <FormattedMessage id="profile.downloadModal.title" defaultMessage="Add new device" />
        </h2>

        <SectionsSwitch
          options={availableSlides}
          selectedOption={selectedSlide}
          name="download-modal-sections"
          className={styles.sectionSwitch}
          optionLabels={{
            [slides.DESKTOP]: (
              <FormattedMessage
                id="profile.downloadModal.desktopSlide.selector"
                defaultMessage="Desktop"
                description="Button that switches instructions to desktop app installation"
              />
            ),
            [slides.MOBILE]: (
              <FormattedMessage
                id="profile.downloadModal.mobileSlide.selector"
                defaultMessage="Mobile"
                description="Button that switches instructions to mobile app installation"
              />
            ),
            [slides.ROUTER]: (
              <FormattedMessage
                id="profile.downloadModal.routerSlide.selector"
                defaultMessage="Router"
                description="Button that switches instructions to router installation"
              />
            ),
          }}
          onSelected={setSelectedSlide}
        />

        <div className={styles.viewPort}>
          <div className={clsx(styles.slider, styles[`sliderPos${selectedSlide}`])}>
            <div
              className={styles.columns}
              data-testid="download-modal-desktop-content"
              aria-hidden={selectedSlide !== slides.DESKTOP}
            >
              <DesktopInstallCol platform={platform} />
              <DesktopPairCol downloadToken={downloadToken} refreshDownloadToken={requestNewDownloadToken} />
            </div>

            <div
              className={styles.columns}
              data-testid="download-modal-mobile-content"
              aria-hidden={selectedSlide !== slides.MOBILE}
            >
              <MobileInstallCol />
              <MobilePairCol downloadToken={downloadToken} refreshDownloadToken={requestNewDownloadToken} />
            </div>

            <DownloadModalRouterSlide onClick={onRouterSlideClick} />
          </div>
        </div>
      </div>
    </Modal>
  );
};

export default DownloadModal;
